import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import UpsellPolicyPicker, { type UpsellPolicy, type UpsellCatalogItem } from '../../../src/components/upsells/UpsellPolicyPicker';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';

interface Location {
  id: string;
  name: string;
}

interface WashTier {
  id: string;
  name: string;
  priceInCents: number;
  isActive: boolean;
}

/** AddBundleScreen - create a prepaid wash bundle product. */
export default function AddBundleScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [submitting, setSubmitting] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [washTiers, setWashTiers] = useState<WashTier[]>([]);
  const [upsellCatalog, setUpsellCatalog] = useState<UpsellCatalogItem[]>([]);
  const [upsellPolicy, setUpsellPolicy] = useState<UpsellPolicy>({}); // 1.4 R05
  // Highest active vacuum master rate ($/min) across the owner's locations —
  // values vac-minute line items in the à-la-carte comparison. null = the
  // owner has no active vacuums yet.
  const [vacRate, setVacRate] = useState<number | null>(null);

  // Form state — line-item builder (mig 0061): one optional wash line plus
  // optional vacuum-minutes and vending-credit lines.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [washEnabled, setWashEnabled] = useState(true);
  const [tierId, setTierId] = useState('');
  const [washCount, setWashCount] = useState('3');
  const [vacEnabled, setVacEnabled] = useState(false);
  const [vacMinutes, setVacMinutes] = useState('30');
  // "Must be used in one session" — stopping early forfeits the remainder
  // instead of refunding unused minutes.
  const [vacAllAtOnce, setVacAllAtOnce] = useState(false);
  const [vendEnabled, setVendEnabled] = useState(false);
  const [vendCredit, setVendCredit] = useState('5.00');
  const [bundlePrice, setBundlePrice] = useState('');
  const [expirationOption, setExpirationOption] = useState('never');
  const [customDays, setCustomDays] = useState('');
  const [visitMinutes, setVisitMinutes] = useState('120');
  const [locationScope, setLocationScope] = useState('all');
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

  const router = useRouter();
  const toast = useToast();
  const scope = useOwnerScope();

  useEffect(() => {
    const ac = new AbortController();
    fetchData(ac.signal);
    return () => ac.abort();
  }, []);

  const fetchData = async (signal?: AbortSignal) => {
    try {
      const locs = await scope.api.get<Location[]>('/api/locations');
      if (signal?.aborted) return;
      setLocations(locs);

      // Wash tiers come from the owner's catalog (source of truth).
      const catalog = await scope.api.get<WashTier[]>('/api/wash-tiers');
      scope.api.get<UpsellCatalogItem[]>('/api/upsells').then(list => setUpsellCatalog(list.filter(u => u.isActive !== false))).catch(() => setUpsellCatalog([]));
      if (signal?.aborted) return;
      const activeTiers = catalog.filter(t => t.isActive);
      setWashTiers(activeTiers);
      if (activeTiers.length > 0) setTierId(activeTiers[0].id);

      // Vacuum master rates (every vacuum now has one — backend contract):
      // the HIGHEST active rate prices vac-minute items in the comparison.
      const baysPerLoc = await Promise.all(locs.map(l =>
        scope.api.get<{ type: string; pricePerMinute: number | null; isActive: boolean }[]>(`/api/locations/${l.id}/bays`)
          .catch(() => [] as { type: string; pricePerMinute: number | null; isActive: boolean }[])));
      if (signal?.aborted) return;
      const rates = baysPerLoc.flat()
        .filter(b => b.type === 'vacuum' && b.isActive && b.pricePerMinute != null && b.pricePerMinute > 0)
        .map(b => b.pricePerMinute as number);
      setVacRate(rates.length > 0 ? Math.max(...rates) : null);
    } catch (err: any) {
      if (!(signal?.aborted)) toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const getExpirationDays = (): number | null => {
    switch (expirationOption) {
      case '30': return 30;
      case '60': return 60;
      case '90': return 90;
      case 'custom': {
        const d = parseInt(customDays);
        return d > 0 ? d : null;
      }
      default: return null;
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Bundle name is required' });
      return;
    }
    if (!washEnabled && !vacEnabled && !vendEnabled) {
      toast.show({ type: 'error', title: 'Error', message: 'Add at least one item to the bundle' });
      return;
    }
    const items: any[] = [];
    if (washEnabled) {
      if (!tierId) {
        toast.show({ type: 'error', title: 'Error', message: 'Select a wash tier' });
        return;
      }
      const count = parseInt(washCount);
      if (!count || count <= 0) {
        toast.show({ type: 'error', title: 'Error', message: 'Enter a valid number of washes' });
        return;
      }
      items.push({ kind: 'wash', tierId, quantity: count });
    }
    if (vacEnabled) {
      const mins = parseInt(vacMinutes);
      if (!mins || mins <= 0) {
        toast.show({ type: 'error', title: 'Error', message: 'Enter valid vacuum minutes' });
        return;
      }
      items.push({ kind: 'vac_minutes', minutes: mins, allAtOnce: vacAllAtOnce });
    }
    if (vendEnabled) {
      const cents = Math.round(parseFloat(vendCredit) * 100);
      if (!cents || cents < 25) {
        toast.show({ type: 'error', title: 'Error', message: 'Enter a valid vending credit amount (at least $0.25)' });
        return;
      }
      items.push({ kind: 'vending_credit', creditCents: cents });
    }
    const priceCents = Math.round(parseFloat(bundlePrice) * 100);
    if (!priceCents || priceCents <= 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter a valid bundle price' });
      return;
    }
    if (locationScope === 'selected' && selectedLocationIds.length === 0) {
      toast.show({ type: 'error', title: 'Error', message: 'Select at least one location' });
      return;
    }
    if (expirationOption === 'custom' && (!customDays || parseInt(customDays) <= 0)) {
      toast.show({ type: 'error', title: 'Error', message: 'Enter valid expiration days' });
      return;
    }
    let expirationMinutes: number | null = null;
    if (expirationOption === 'visit') {
      const mins = parseInt(visitMinutes);
      if (!mins || mins < 15 || mins > 10080) {
        toast.show({ type: 'error', title: 'Error', message: 'Same-visit window must be 15–10080 minutes' });
        return;
      }
      expirationMinutes = mins;
    }

    try {
      setSubmitting(true);
      await scope.api.post('/api/bundle-plans', {
        name: name.trim(),
        description: description.trim() || undefined,
        items,
        upsellPolicy,
        bundlePrice: priceCents,
        locationScope,
        locationIds: locationScope === 'selected' ? selectedLocationIds : undefined,
        // Exclusive: same-visit window (minutes) OR fixed days OR never.
        expirationDays: expirationMinutes != null ? null : getExpirationDays(),
        expirationMinutes,
      });
      toast.show({ type: 'success', title: 'Bundle Created' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleLocationId = (id: string) => {
    setSelectedLocationIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // À-la-carte comparison: wash tier price × count, plus vending credit face
  // value, plus vac minutes at the owner's highest active vacuum master rate.
  // No vacuums yet → the vac line is noted but unpriced.
  const tierRetailPrice = washTiers.find(t => t.id === tierId)?.priceInCents || 0;
  const count = parseInt(washCount) || 0;
  const washValue = washEnabled ? tierRetailPrice * count : 0;
  const vendValue = vendEnabled ? Math.round((parseFloat(vendCredit) || 0) * 100) : 0;
  const vacMins = parseInt(vacMinutes) || 0;
  const vacValue = vacEnabled && vacRate != null ? Math.round(vacMins * vacRate * 100) : 0;
  const retailValue = washValue + vendValue + vacValue;
  const priceCents = Math.round((parseFloat(bundlePrice) || 0) * 100);
  const savings = retailValue > 0 && priceCents > 0 ? Math.round(((retailValue - priceCents) / retailValue) * 100) : 0;

  const expirationOptions = [
    { label: 'Never Expires', value: 'never' },
    { label: '30 Days', value: '30' },
    { label: '60 Days', value: '60' },
    { label: '90 Days', value: '90' },
    { label: 'Custom Days', value: 'custom' },
    { label: 'Same Visit (minutes)', value: 'visit' },
  ];

  const tierOptions = washTiers.map(t => ({ label: `${t.name} ($${(t.priceInCents / 100).toFixed(2)})`, value: t.id }));

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer>
      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Bundle Details</Text>

        <Input label="Bundle Name" value={name} onChangeText={setName} placeholder='e.g. "Wash + Vac Combo"' />
        <Input label="Description (optional)" value={description} onChangeText={setDescription} placeholder="Describe the bundle" multiline maxLength={500} />
      </Card>

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Bundle Items</Text>
        <Text style={styles.hintText}>Add at least one line. One wash line maximum.</Text>

        {/* Wash line */}
        <TouchableOpacity style={styles.itemToggleRow} onPress={() => setWashEnabled(!washEnabled)}>
          <Ionicons name={washEnabled ? 'checkbox' : 'square-outline'} size={24} color={washEnabled ? colors.primary : colors.textLight} />
          <Ionicons name="car" size={18} color={colors.textSecondary} />
          <Text style={styles.itemToggleLabel}>Washes</Text>
        </TouchableOpacity>
        {washEnabled && (
          <View style={styles.itemBody}>
            {tierOptions.length > 0 ? (
              <>
              <Picker label="Wash Tier" options={tierOptions} value={tierId} onValueChange={setTierId} />
              <UpsellPolicyPicker catalog={upsellCatalog} value={upsellPolicy} onChange={setUpsellPolicy} title="Add-ons with bundle washes" hint="Included = free with every bundle wash. Purchasable = offered at the regular price. Not offered = hidden." />
              </>
            ) : (
              <View style={{ marginBottom: spacing.md }}>
                <Text style={styles.label}>Wash Tier</Text>
                <Text style={styles.hintText}>No wash tiers yet. Create them under "Manage Wash Tiers" first.</Text>
              </View>
            )}
            <Input
              label="Number of Washes"
              value={washCount}
              onChangeText={(v: string) => setWashCount(v.replace(/[^0-9]/g, ''))}
              placeholder="3"
              keyboardType="number-pad"
            />
          </View>
        )}

        {/* Vacuum minutes line */}
        <TouchableOpacity style={styles.itemToggleRow} onPress={() => setVacEnabled(!vacEnabled)}>
          <Ionicons name={vacEnabled ? 'checkbox' : 'square-outline'} size={24} color={vacEnabled ? colors.primary : colors.textLight} />
          <Ionicons name="sparkles" size={18} color={colors.textSecondary} />
          <Text style={styles.itemToggleLabel}>Vacuum Minutes</Text>
        </TouchableOpacity>
        {vacEnabled && (
          <View style={styles.itemBody}>
            <Input
              label="Vacuum Minutes"
              value={vacMinutes}
              onChangeText={(v: string) => setVacMinutes(v.replace(/[^0-9]/g, ''))}
              placeholder="30"
              keyboardType="number-pad"
            />
            <TouchableOpacity style={styles.itemToggleRow} onPress={() => setVacAllAtOnce(!vacAllAtOnce)}>
              <Ionicons name={vacAllAtOnce ? 'checkbox' : 'square-outline'} size={22} color={vacAllAtOnce ? colors.primary : colors.textLight} />
              <Text style={styles.itemToggleLabel}>Must be used in one session</Text>
            </TouchableOpacity>
            <Text style={styles.hintText}>
              The customer uses all the minutes in a single vacuum session — stopping early forfeits the rest.
            </Text>
          </View>
        )}

        {/* Vending credit line */}
        <TouchableOpacity style={styles.itemToggleRow} onPress={() => setVendEnabled(!vendEnabled)}>
          <Ionicons name={vendEnabled ? 'checkbox' : 'square-outline'} size={24} color={vendEnabled ? colors.primary : colors.textLight} />
          <Ionicons name="fast-food" size={18} color={colors.textSecondary} />
          <Text style={styles.itemToggleLabel}>Vending Credit</Text>
        </TouchableOpacity>
        {vendEnabled && (
          <View style={styles.itemBody}>
            <Input
              label="Vending Credit ($)"
              value={vendCredit}
              onChangeText={(v: string) => setVendCredit(v.replace(/[^0-9.]/g, ''))}
              placeholder="5.00"
              keyboardType="decimal-pad"
            />
          </View>
        )}
      </Card>

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Price</Text>
        <Input
          label="Bundle Price ($)"
          value={bundlePrice}
          onChangeText={setBundlePrice}
          placeholder="24.99"
          keyboardType="decimal-pad"
        />

        {/* À-la-carte comparison */}
        {retailValue > 0 && priceCents > 0 && (
          <View style={styles.savingsBox}>
            <Text style={styles.savingsLabel}>
              À-la-carte value: ${(retailValue / 100).toFixed(2)}
              {' ('}
              {[
                washEnabled && count > 0 ? `${count} × $${(tierRetailPrice / 100).toFixed(2)} washes` : null,
                vacEnabled && vacValue > 0 ? `${vacMins} vac min × $${(vacRate ?? 0).toFixed(2)}/min` : null,
                vendEnabled && vendValue > 0 ? `$${(vendValue / 100).toFixed(2)} vending credit` : null,
              ].filter(Boolean).join(' + ')}
              {')'}
              {vacEnabled && vacMins > 0 && vacRate == null ? ` + ${vacMins} vac min (add a vacuum to price this)` : ''}
            </Text>
            {savings > 0 ? (
              <Text style={[styles.savingsPercent, { color: colors.success }]}>
                {savings}% savings for customer{vacEnabled && vacRate == null ? ' (before vacuum value)' : ''}
              </Text>
            ) : (
              <Text style={[styles.savingsPercent, { color: colors.warning }]}>
                No discount — price equals or exceeds the priced items
              </Text>
            )}
          </View>
        )}
      </Card>

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Expiration</Text>
        <Picker label="Bundle Expires After" options={expirationOptions} value={expirationOption} onValueChange={setExpirationOption} />
        {expirationOption === 'custom' && (
          <Input label="Days Until Expiry" value={customDays} onChangeText={setCustomDays} placeholder="45" keyboardType="number-pad" />
        )}
        {expirationOption === 'visit' && (
          <>
            <Input label="Same-Visit Window (minutes)" value={visitMinutes} onChangeText={(v: string) => setVisitMinutes(v.replace(/[^0-9]/g, ''))} placeholder="120" keyboardType="number-pad" />
            <Text style={styles.hintText}>
              The bundle expires this many minutes after purchase — for combos meant to be used during one visit.
            </Text>
          </>
        )}
      </Card>

      <Card style={styles.formCard}>
        <Text style={styles.sectionTitle}>Location Scope</Text>
        <Text style={styles.label}>Where can this bundle be used?</Text>
        <View style={{ flexDirection: 'row', gap: spacing.sm, marginBottom: spacing.sm }}>
          {(['all', 'selected'] as const).map(scope => (
            <TouchableOpacity
              key={scope}
              onPress={() => setLocationScope(scope)}
              style={[styles.scopeChip, locationScope === scope && { backgroundColor: colors.primary, borderColor: colors.primary }]}
            >
              <Text style={[styles.scopeChipText, locationScope === scope && { color: '#fff' }]}>
                {scope === 'all' ? 'All Locations' : 'Selected'}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {locationScope === 'selected' && locations.length > 0 && (
          <View style={{ gap: spacing.xs }}>
            {locations.map(loc => (
              <TouchableOpacity key={loc.id} onPress={() => toggleLocationId(loc.id)} style={[styles.locOption, selectedLocationIds.includes(loc.id) && { borderColor: colors.primary, backgroundColor: colors.primary + '10' }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                  <Ionicons name={selectedLocationIds.includes(loc.id) ? 'checkbox' : 'square-outline'} size={20} color={selectedLocationIds.includes(loc.id) ? colors.primary : colors.textLight} />
                  <Text style={[styles.locOptionText, selectedLocationIds.includes(loc.id) && { color: colors.primary, fontWeight: fontWeight.semibold }]}>{loc.name}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        )}
      </Card>

      <Button
        title="Create Bundle"
        onPress={handleSubmit}
        loading={submitting}
        size="lg"
        style={{ marginTop: spacing.md }}
      />

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  formCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  label: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text, marginBottom: spacing.xs },
  hintText: { fontSize: fontSize.sm, color: c.textLight, fontStyle: 'italic' },
  savingsBox: { backgroundColor: c.background, borderRadius: borderRadius.md, padding: spacing.md, marginBottom: spacing.md },
  itemToggleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  itemToggleLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  itemBody: { paddingLeft: spacing.xl, marginBottom: spacing.xs },
  savingsLabel: { fontSize: fontSize.sm, color: c.textSecondary },
  savingsPercent: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, marginTop: spacing.xs },
  scopeChip: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  scopeChipText: { fontSize: fontSize.sm, color: c.text },
  locOption: { paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, borderWidth: 1, borderColor: c.borderLight },
  locOptionText: { fontSize: fontSize.sm, color: c.text },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
